/**
 * Typed API surface.
 *
 * Every function's argument and return types come from @swi/contracts — there is
 * no hand-written request or response interface anywhere in this app. Changing a
 * DTO in the contract package breaks this file at compile time, which is the
 * point.
 */
import {
  AirshipResponseSchema,
  AirshipWithAvailabilitySchema,
  AuditEventSchema,
  AuthResponseSchema,
  AvailabilityResponseSchema,
  CalendarResponseSchema,
  DashboardLiveResponseSchema,
  DashboardStatsResponseSchema,
  HubResponseSchema,
  MaintenanceWindowSchema,
  NotificationHealthSchema,
  NotificationLogSchema,
  PriceBreakdownSchema,
  ReservationResponseSchema,
  ReservationSummarySchema,
  TelemetryPointSchema,
  UserResponseSchema,
  pageResponseSchema,
  type AirshipListQuery,
  type AirshipResponse,
  type AirshipWithAvailability,
  type AuditEvent,
  type AuditListQuery,
  type AuthResponse,
  type AvailabilityResponse,
  type CalendarResponse,
  type ApproveReservationRequest,
  type CancelReservationRequest,
  type CreateAirshipRequest,
  type CreateMaintenanceWindowRequest,
  type CreateReservationRequest,
  type HubResponse,
  type IsoInstant,
  type LoginRequest,
  type MaintenanceWindow,
  type NotificationHealth,
  type NotificationListQuery,
  type NotificationLog,
  type PageResponse,
  type PriceBreakdown,
  type QuoteRequest,
  type RegisterRequest,
  type RejectReservationRequest,
  type ReservationListQuery,
  type ReservationResponse,
  type ReservationSummary,
  type TelemetryPoint,
  type UpdateAirshipRequest,
  type UpdateUserRequest,
  type UserResponse,
} from '@swi/contracts';
import { query, request } from './client';

/* ---------------------------------- auth ---------------------------------- */

export const login = (body: LoginRequest): Promise<AuthResponse> =>
  request('/auth/login', { method: 'POST', body, schema: AuthResponseSchema });

export const register = (body: RegisterRequest): Promise<AuthResponse> =>
  request('/auth/register', { method: 'POST', body, schema: AuthResponseSchema });

export const me = (): Promise<UserResponse> =>
  request('/auth/me', { schema: UserResponseSchema });

/* ---------------------------------- hubs ----------------------------------- */

export const listHubs = (): Promise<HubResponse[]> =>
  request('/hubs', { schema: HubResponseSchema.array() });

/* --------------------------------- airships -------------------------------- */

export const listAirships = (
  params: Partial<AirshipListQuery> = {},
): Promise<AirshipWithAvailability[]> =>
  request(
    `/airships${query({
      hub: params.hub,
      airshipClass: params.airshipClass,
      status: params.status,
      minCapacity: params.minCapacity,
      maxHourlyRate: params.maxHourlyRate,
      from: params.from,
      to: params.to,
    })}`,
    { schema: AirshipWithAvailabilitySchema.array() },
  );

export const getAirship = (id: number): Promise<AirshipResponse> =>
  request(`/airships/${String(id)}`, { schema: AirshipResponseSchema });

export const createAirship = (body: CreateAirshipRequest): Promise<AirshipResponse> =>
  request('/airships', { method: 'POST', body, schema: AirshipResponseSchema });

export const updateAirship = (
  id: number,
  body: UpdateAirshipRequest,
): Promise<AirshipResponse> =>
  request(`/airships/${String(id)}`, { method: 'PATCH', body, schema: AirshipResponseSchema });

export const listMaintenance = (id: number): Promise<MaintenanceWindow[]> =>
  request(`/airships/${String(id)}/maintenance`, { schema: MaintenanceWindowSchema.array() });

export const addMaintenance = (
  id: number,
  body: CreateMaintenanceWindowRequest,
): Promise<MaintenanceWindow> =>
  request(`/airships/${String(id)}/maintenance`, {
    method: 'POST',
    body,
    schema: MaintenanceWindowSchema,
  });

/* ------------------------------- availability ------------------------------ */

export const checkAvailability = (params: {
  airshipId: number;
  from: IsoInstant;
  to: IsoInstant;
  passengers?: number;
  excludeReservationId?: string;
}): Promise<AvailabilityResponse> =>
  request(`/availability${query({ ...params })}`, { schema: AvailabilityResponseSchema });

export const getCalendar = (params: {
  airshipId: number;
  from: IsoInstant;
  to: IsoInstant;
}): Promise<CalendarResponse> =>
  request(`/availability/calendar${query({ ...params })}`, { schema: CalendarResponseSchema });

/* ------------------------------- reservations ------------------------------ */

export const createReservation = (body: CreateReservationRequest): Promise<ReservationResponse> =>
  request('/reservations', { method: 'POST', body, schema: ReservationResponseSchema });

export const listReservations = (
  params: Partial<ReservationListQuery> = {},
): Promise<PageResponse<ReservationSummary>> =>
  request(
    `/reservations${query({
      page: params.page ?? 1,
      pageSize: params.pageSize ?? 25,
      state: params.state,
      airshipId: params.airshipId,
      mine: params.mine,
      from: params.from,
      to: params.to,
    })}`,
    { schema: pageResponseSchema(ReservationSummarySchema) },
  );

export const getReservation = (id: string): Promise<ReservationResponse> =>
  request(`/reservations/${id}`, { schema: ReservationResponseSchema });

export const quote = (body: QuoteRequest): Promise<PriceBreakdown> =>
  request('/reservations/quote', { method: 'POST', body, schema: PriceBreakdownSchema });

export const confirmReservation = (id: string): Promise<ReservationResponse> =>
  request(`/reservations/${id}/confirm`, { method: 'POST', schema: ReservationResponseSchema });

export const cancelReservation = (
  id: string,
  body: CancelReservationRequest,
): Promise<ReservationResponse> =>
  request(`/reservations/${id}/cancel`, {
    method: 'POST',
    body,
    schema: ReservationResponseSchema,
  });

/** OP-05 — fleet owner only. */
export const approveReservation = (
  id: string,
  body: ApproveReservationRequest = {},
): Promise<ReservationResponse> =>
  request(`/reservations/${id}/approve`, {
    method: 'POST',
    body,
    schema: ReservationResponseSchema,
  });

export const rejectReservation = (
  id: string,
  body: RejectReservationRequest,
): Promise<ReservationResponse> =>
  request(`/reservations/${id}/reject`, {
    method: 'POST',
    body,
    schema: ReservationResponseSchema,
  });

export const startReservation = (id: string): Promise<ReservationResponse> =>
  request(`/reservations/${id}/start`, { method: 'POST', schema: ReservationResponseSchema });

export const completeReservation = (id: string): Promise<ReservationResponse> =>
  request(`/reservations/${id}/complete`, { method: 'POST', schema: ReservationResponseSchema });

export const getTelemetry = (id: string): Promise<TelemetryPoint[]> =>
  request(`/reservations/${id}/telemetry`, { schema: TelemetryPointSchema.array() });

/* --------------------------------- dashboard ------------------------------- */

export const getLive = () =>
  request('/dashboard/live', { schema: DashboardLiveResponseSchema });

export const getStats = () =>
  request('/dashboard/stats', { schema: DashboardStatsResponseSchema });

/* ------------------------------- notifications ----------------------------- */

export const listNotifications = (
  params: Partial<NotificationListQuery> = {},
): Promise<PageResponse<NotificationLog>> =>
  request(
    `/notifications${query({
      page: params.page ?? 1,
      pageSize: params.pageSize ?? 25,
      reservationId: params.reservationId,
      status: params.status,
    })}`,
    { schema: pageResponseSchema(NotificationLogSchema) },
  );

export const notificationHealth = (): Promise<NotificationHealth> =>
  request('/notifications/health', { schema: NotificationHealthSchema });

export const retryNotification = (id: number): Promise<NotificationLog> =>
  request(`/notifications/${String(id)}/retry`, { method: 'POST', schema: NotificationLogSchema });

/* ----------------------------------- audit --------------------------------- */

export const listAudit = (
  params: Partial<AuditListQuery> = {},
): Promise<PageResponse<AuditEvent>> =>
  request(
    `/audit${query({
      page: params.page ?? 1,
      pageSize: params.pageSize ?? 50,
      entityId: params.entityId,
      entityType: params.entityType,
      actorId: params.actorId,
    })}`,
    { schema: pageResponseSchema(AuditEventSchema) },
  );

/* ----------------------------------- users --------------------------------- */

export const listUsers = (): Promise<UserResponse[]> =>
  request('/users', { schema: UserResponseSchema.array() });

export const listPilots = (): Promise<UserResponse[]> =>
  request('/users/pilots', { schema: UserResponseSchema.array() });

export const updateUser = (id: number, body: UpdateUserRequest): Promise<UserResponse> =>
  request(`/users/${String(id)}`, { method: 'PATCH', body, schema: UserResponseSchema });
